import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { storesApi, accountsApi, healthApi } from '../services/api';
import { Map } from '../components/Map';
import { StoreEditDrawer } from '../components/StoreEditDrawer';
import { AccountEditDrawer } from '../components/AccountEditDrawer';
import type { StoreWithPriority, AccountWithStores, StoreHealthData } from 'shared';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getHoursStatus(hours: Record<string, [number, number]>): { isOpen: boolean; label: string } {
  const now = new Date();
  const todayKey = String(now.getDay());
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const todayHours = hours[todayKey];
  if (!todayHours) return { isOpen: false, label: 'Closed today' };
  const [open, close] = todayHours;
  if (currentMin >= open && currentMin < close) return { isOpen: true, label: `Closes ${formatMinutes(close)}` };
  if (currentMin < open) return { isOpen: false, label: `Opens ${formatMinutes(open)}` };
  return { isOpen: false, label: 'Closed for the day' };
}

function StoreInfoPanel({
  store,
  health,
  onClose,
  onEdit,
  onSyncHours,
}: {
  store: StoreWithPriority;
  health?: StoreHealthData | null;
  onClose: () => void;
  onEdit: () => void;
  onSyncHours: () => void;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(store.notes ?? '');
  const [syncingHours, setSyncingHours] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshHealthMutation = useMutation({
    mutationFn: () => healthApi.refreshStoreHealth(store.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores-health'] });
    },
  });

  useEffect(() => { setNotes(store.notes ?? ''); }, [store.id, store.notes]);

  const saveNotes = useCallback((value: string) => {
    storesApi.update(store.id, { notes: value || undefined }).then(() => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    });
  }, [store.id, queryClient]);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveNotes(value), 1000);
  };

  const handleSyncHours = async () => {
    setSyncingHours(true);
    try {
      await onSyncHours();
    } finally {
      setSyncingHours(false);
    }
  };

  const todayIdx = new Date().getDay();
  const hoursStatus = store.opening_hours ? getHoursStatus(store.opening_hours) : null;

  const fullAddress = [store.address_line1, store.city, store.state, store.postal_code]
    .filter(Boolean).join(', ');

  const googleMapsUrl = store.place_id
    ? `https://www.google.com/maps/place/?q=place_id:${store.place_id}`
    : `https://www.google.com/maps/search/${encodeURIComponent(`${store.name} ${fullAddress}`)}`;

  const yelpUrl = `https://www.yelp.com/search?find_desc=${encodeURIComponent(store.name)}&find_loc=${encodeURIComponent([store.city, store.state].filter(Boolean).join(', '))}`;

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full overflow-hidden shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 text-sm leading-snug">{store.name}</h2>
            {store.chain_name && (
              <p className="text-xs text-gray-500 mt-0.5">{store.chain_name}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Edit store"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Address */}
        {fullAddress && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Address</p>
            <p className="text-sm text-gray-800 leading-snug">{fullAddress}</p>
          </div>
        )}

        {/* Health */}
        {store.place_id && (
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Store Health</p>
              <button
                onClick={() => refreshHealthMutation.mutate()}
                disabled={refreshHealthMutation.isPending}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                <svg className={`w-3 h-3 ${refreshHealthMutation.isPending ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            {health ? (
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-bold ${
                  (health.health_score ?? 0) >= 80 ? 'text-green-600' :
                  (health.health_score ?? 0) >= 60 ? 'text-lime-600' :
                  (health.health_score ?? 0) >= 40 ? 'text-yellow-600' : 'text-red-600'
                }`}>{health.health_score ?? '—'}</span>
                <div>
                  <div className={`text-sm font-medium ${
                    health.health_trend === 'growing' ? 'text-green-600' :
                    health.health_trend === 'declining' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {health.health_trend === 'growing' ? '↑ Growing' :
                     health.health_trend === 'declining' ? '↓ Declining' : '→ Stable'}
                  </div>
                  {health.google_rating != null && (
                    <div className="text-xs text-gray-500">
                      ★ {health.google_rating.toFixed(1)}
                      {health.google_review_count != null && ` · ${health.google_review_count.toLocaleString()} reviews`}
                    </div>
                  )}
                  {health.last_enriched_at && (
                    <div className="text-xs text-gray-400">
                      Updated {new Date(health.last_enriched_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No health data — click Refresh</p>
            )}
          </div>
        )}

        {/* Hours */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</p>
            {store.place_id && (
              <button
                onClick={handleSyncHours}
                disabled={syncingHours}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
                title="Refresh hours from Google"
              >
                <svg className={`w-3 h-3 ${syncingHours ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync
              </button>
            )}
          </div>

          {store.opening_hours ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${hoursStatus!.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {hoursStatus!.isOpen ? 'Open' : 'Closed'}
                </span>
                <span className="text-xs text-gray-500">{hoursStatus!.label}</span>
              </div>
              <table className="w-full text-xs border-collapse">
                <tbody>
                  {DAY_NAMES.map((day, i) => {
                    const h = store.opening_hours![String(i)];
                    const isToday = i === todayIdx;
                    return (
                      <tr key={day} className={isToday ? 'font-semibold text-gray-900' : 'text-gray-600'}>
                        <td className="py-0.5 pr-3 w-8">{DAY_SHORT[i]}</td>
                        <td>{h ? `${formatMinutes(h[0])} – ${formatMinutes(h[1])}` : 'Closed'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <p className="text-xs text-gray-400 italic mb-2">
              {store.place_id ? 'No hours — click Sync to fetch from Google' : 'No hours on file'}
            </p>
          )}

          {/* Verify hours on external sources */}
          <div className="flex flex-col gap-1.5 mt-3">
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-blue-600 transition-colors"
            >
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              Verify on Google Maps
            </a>
            <a
              href={yelpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-red-500 transition-colors"
            >
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.16 12.73l-4.46 1.39c-.53.17-1.08-.23-1.08-.8V8.14c0-.63.67-1.04 1.22-.75l4.46 2.39c.59.32.59 1.17-.14 1.95zM9.32 15.13l-1.07 4.59c-.15.64.52 1.16 1.1.84l4.1-2.28c.47-.26.54-.91.15-1.28L10.5 14c-.44-.41-1.1-.24-1.18.63v.5zm-4.9-3.26l4.59 1.07c.64.15 1.16-.52.84-1.1L7.57 7.74c-.26-.47-.91-.54-1.28-.15L3.29 10.69c-.41.44-.24 1.1.63 1.18h.5zm5.5-5.74L8.85 1.54C8.53.95 7.79.96 7.47 1.54L5.19 5.64c-.23.42.02.94.5 1.03l4.59.87c.64.12 1.1-.55.74-1.07l-.1-.34zm5.62 9.26c.41-.44.24-1.1-.63-1.18h-.5l-4.59-1.07c-.64-.15-1.16.52-.84 1.1l2.28 4.1c.26.47.91.54 1.28.15l2.99-3.1z"/>
              </svg>
              Search on Yelp
            </a>
          </div>
        </div>

        {/* Website & Phone */}
        {(store.website || store.phone_number) && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Contact</p>
            <div className="flex flex-col gap-2">
              {store.website && (
                <a
                  href={store.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 min-w-0"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                  </svg>
                  <span className="truncate">{store.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                </a>
              )}
              {store.phone_number && (
                <a
                  href={`tel:${store.phone_number}`}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {store.phone_number}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="px-4 py-3 flex flex-col flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</p>
          <textarea
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add notes about this store…"
            rows={6}
            className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
          />
          <p className="text-xs text-gray-400 mt-1">Auto-saved</p>
        </div>
      </div>
    </div>
  );
}

export function MyStores() {
  const queryClient = useQueryClient();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [editingStore, setEditingStore] = useState<StoreWithPriority | null>(null);
  const [editingAccount, setEditingAccount] = useState<AccountWithStores | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');

  const { data: accounts = [], isLoading: loadingAccounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const { data: stores = [], isLoading: loadingStores } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  const { data: healthList = [] } = useQuery({
    queryKey: ['stores-health'],
    queryFn: () => healthApi.listStoreHealth(),
    staleTime: 5 * 60 * 1000,
  });

  const healthMap = useMemo(() => {
    const map: Record<string, StoreHealthData> = {};
    for (const h of healthList) {
      if (h.store_id) map[h.store_id] = h;
    }
    return map;
  }, [healthList]);

  const [healthToast, setHealthToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  function showHealthToast(message: string, type: 'success' | 'error') {
    setHealthToast({ message, type });
    setTimeout(() => setHealthToast(null), 4000);
  }

  const bulkRefreshMutation = useMutation({
    mutationFn: () => healthApi.bulkRefreshHealth(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stores-health'] });
      if (result.errors > 0) {
        showHealthToast(`${result.refreshed} refreshed, ${result.skipped} skipped, ${result.errors} failed`, 'error');
      } else {
        showHealthToast(`${result.refreshed} store${result.refreshed !== 1 ? 's' : ''} refreshed${result.skipped > 0 ? `, ${result.skipped} already up to date` : ''}`, 'success');
      }
    },
    onError: () => showHealthToast('Failed to refresh health data', 'error'),
  });

  const createAccountMutation = useMutation({
    mutationFn: (name: string) => accountsApi.create({ name }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setShowNewAccount(false);
      setNewAccountName('');
      setExpandedAccounts((prev) => new Set([...prev, created.id]));
    },
  });

  const standaloneStores = useMemo(
    () => stores.filter((s) => !s.account_id).sort((a, b) => a.name.localeCompare(b.name)),
    [stores]
  );

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts]
  );

  const selectedStore = stores.find((s) => s.id === selectedStoreId);

  const toggleAccount = (id: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStoreSelect = (storeId: string) => {
    setSelectedStoreId((prev) => (prev === storeId ? null : storeId));
  };

  const handleSyncHours = async () => {
    if (!selectedStoreId) return;
    await storesApi.syncHours(selectedStoreId);
    queryClient.invalidateQueries({ queryKey: ['stores'] });
  };

  const isLoading = loadingAccounts || loadingStores;

  const totalCount = stores.length;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin w-8 h-8 mx-auto mb-4 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-600">Loading stores...</p>
        </div>
      </div>
    );
  }

  if (stores.length === 0 && accounts.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Stores Yet</h2>
          <p className="text-gray-600 mb-6">Add stores to your territory to start scheduling visits.</p>
          <Link
            to="/stores/find"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Find Stores
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full relative">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-[1000]"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <div className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:relative inset-y-0 left-0 z-[1002]
        w-80 sm:w-96 bg-white border-r border-gray-200 flex flex-col
        transition-transform duration-300 ease-in-out
        lg:transform-none
      `}>
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">My Stores</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => bulkRefreshMutation.mutate()}
                disabled={bulkRefreshMutation.isPending}
                className="text-xs text-gray-500 hover:text-blue-600 disabled:opacity-50 flex items-center gap-1"
                title="Refresh health data for all stores"
              >
                <svg
                  className={`w-3.5 h-3.5 ${bulkRefreshMutation.isPending ? 'animate-spin' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Health
              </button>
              <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {totalCount}
              </span>
            </div>
          </div>

          {/* New Account inline form */}
          {showNewAccount ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newAccountName.trim()) createAccountMutation.mutate(newAccountName.trim());
              }}
              className="flex gap-2 mt-2"
            >
              <input
                autoFocus
                type="text"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                placeholder="Account name"
                className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={createAccountMutation.isPending || !newAccountName.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setShowNewAccount(false); setNewAccountName(''); }}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              onClick={() => setShowNewAccount(true)}
              className="w-full mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Account
            </button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">

          {/* Parent Accounts */}
          {sortedAccounts.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Accounts</p>
              </div>
              {sortedAccounts.map((account) => {
                const isExpanded = expandedAccounts.has(account.id);
                return (
                  <div key={account.id} className="border-b border-gray-100">
                    {/* Account header row */}
                    <div className="flex items-center gap-2 px-3 py-3 hover:bg-gray-50">
                      <button
                        onClick={() => toggleAccount(account.id)}
                        className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => toggleAccount(account.id)}
                      >
                        <p className="font-medium text-gray-900 truncate text-sm">{account.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {account.priority?.account_size && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              account.priority.account_size === 'large'
                                ? 'bg-purple-100 text-purple-700'
                                : account.priority.account_size === 'medium'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {account.priority.account_size}
                            </span>
                          )}
                          {account.priority?.is_lapsed && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                              lapsed
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {account.stores.length} store{account.stores.length !== 1 ? 's' : ''}
                          </span>
                          {account.contact_name && (
                            <span className="text-xs text-gray-400 truncate">{account.contact_name}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingAccount(account); }}
                        className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit account"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>

                    {/* Child stores */}
                    {isExpanded && account.stores.length > 0 && (
                      <div className="border-t border-gray-100 bg-gray-50/50">
                        {[...account.stores].sort((a, b) => a.name.localeCompare(b.name)).map((store) => (
                          <div
                            key={store.id}
                            onClick={() => {
                              handleStoreSelect(store.id);
                              if (window.innerWidth < 1024) setSidebarOpen(false);
                            }}
                            className={`flex items-center gap-2 pl-10 pr-3 py-2.5 cursor-pointer transition-colors ${
                              selectedStoreId === store.id
                                ? 'bg-blue-50 border-l-4 border-blue-600'
                                : 'hover:bg-gray-100 border-l-4 border-transparent'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{store.name}</p>
                              {(store.address_line1 || store.city) && (
                                <p className="text-xs text-gray-500 truncate">
                                  {store.address_line1}{store.city && `, ${store.city}`}
                                </p>
                              )}
                            </div>
                            {(() => {
                              const h = healthMap[store.id];
                              if (!h?.health_score) return null;
                              const color =
                                h.health_score >= 80 ? 'text-green-700 bg-green-50' :
                                h.health_score >= 60 ? 'text-lime-700 bg-lime-50' :
                                h.health_score >= 40 ? 'text-yellow-700 bg-yellow-50' :
                                'text-red-700 bg-red-50';
                              return (
                                <span className={`shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded ${color}`}>
                                  {h.health_score}
                                </span>
                              );
                            })()}
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingStore(store); }}
                              className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Edit store"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {isExpanded && account.stores.length === 0 && (
                      <div className="pl-10 pr-3 py-2.5 bg-gray-50/50">
                        <p className="text-xs text-gray-400 italic">No stores linked yet</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Standalone Stores */}
          {standaloneStores.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Standalone Stores</p>
              </div>
              <div className="divide-y divide-gray-100">
                {standaloneStores.map((store) => (
                  <div
                    key={store.id}
                    onClick={() => {
                      handleStoreSelect(store.id);
                      if (window.innerWidth < 1024) setSidebarOpen(false);
                    }}
                    className={`p-3 sm:p-4 cursor-pointer transition-colors ${
                      selectedStoreId === store.id
                        ? 'bg-blue-50 border-l-4 border-blue-600'
                        : 'hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate text-sm">{store.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {store.address_line1}{store.city && `, ${store.city}`}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          {store.priority?.account_size && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              store.priority.account_size === 'large'
                                ? 'bg-purple-100 text-purple-700'
                                : store.priority.account_size === 'medium'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {store.priority.account_size}
                            </span>
                          )}
                          {store.priority?.is_lapsed && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                              lapsed
                            </span>
                          )}
                          {store.priority && store.priority.ytd_revenue_cents > 0 && (
                            <span className="text-xs text-gray-500">
                              ${(store.priority.ytd_revenue_cents / 100).toLocaleString()} YTD
                            </span>
                          )}
                          {(() => {
                            const h = healthMap[store.id];
                            if (!h?.health_score) return null;
                            const color =
                              h.health_score >= 80 ? 'text-green-700 bg-green-50' :
                              h.health_score >= 60 ? 'text-lime-700 bg-lime-50' :
                              h.health_score >= 40 ? 'text-yellow-700 bg-yellow-50' :
                              'text-red-700 bg-red-50';
                            const trendIcon = h.health_trend === 'growing' ? '↑' : h.health_trend === 'declining' ? '↓' : '→';
                            return (
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${color}`}>
                                {h.health_score} {trendIcon}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingStore(store); }}
                        className="shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit store"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-gray-200">
          <Link
            to="/stores/find"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add More Stores</span>
          </Link>
        </div>
      </div>

      {/* Map + right panel wrapper */}
      <div className="flex flex-1 min-w-0 relative overflow-hidden">
      {/* Map */}
      <div className="flex-1 relative">
        {healthToast && (
          <div className={`absolute top-3 right-3 z-[1001] text-sm px-4 py-2 rounded-lg shadow-md border ${
            healthToast.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {healthToast.message}
          </div>
        )}
        <button
          onClick={() => setSidebarOpen(true)}
          className={`
            lg:hidden absolute top-3 left-3 z-[1001]
            flex items-center gap-2 px-3 py-2
            bg-white rounded-lg shadow-lg
            text-sm font-medium text-gray-700
            hover:bg-gray-50 transition-colors
            ${sidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span>{totalCount} Stores</span>
        </button>

        <button
          onClick={() => setShowHeatmap(h => !h)}
          className="absolute bottom-6 left-4 z-[1001] flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          title="Toggle population heatmap"
        >
          <span className="w-3 h-3 rounded-sm" style={{ background: 'linear-gradient(to right, #e5e7eb, #16a34a)' }} />
          {showHeatmap ? 'Hide Heatmap' : 'Show Heatmap'}
        </button>

        <Map
          showHeatmap={showHeatmap}
          markers={stores.map((s) => {
            const h = healthMap[s.id];
            return {
              id: s.id,
              lat: s.lat,
              lng: s.lng,
              name: s.name,
              address: s.address_line1 ? `${s.address_line1}${s.city ? `, ${s.city}` : ''}` : undefined,
              selected: s.id === selectedStoreId,
              healthScore: h?.health_score ?? null,
              healthTrend: h?.health_trend ?? null,
              googleRating: h?.google_rating ?? null,
              googleReviewCount: h?.google_review_count ?? null,
              reviewVelocity: h?.google_review_velocity ?? null,
              lastEnrichedAt: h?.last_enriched_at ?? null,
              placeId: s.place_id,
              openingHours: s.opening_hours ?? null,
              website: s.website ?? null,
              phoneNumber: s.phone_number ?? null,
            };
          })}
          onMarkerClick={handleStoreSelect}
          center={selectedStore ? { lat: selectedStore.lat, lng: selectedStore.lng } : undefined}
          zoom={selectedStore ? 15 : 10}
          showBoundary
        />
      </div>

      {/* Right info panel */}
      {selectedStore && (
        <StoreInfoPanel
          store={selectedStore}
          health={healthMap[selectedStore.id] ?? null}
          onClose={() => setSelectedStoreId(null)}
          onEdit={() => setEditingStore(selectedStore)}
          onSyncHours={handleSyncHours}
        />
      )}
      </div>

      {editingStore && (
        <StoreEditDrawer
          store={editingStore}
          onClose={() => setEditingStore(null)}
        />
      )}

      {editingAccount && (
        <AccountEditDrawer
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
        />
      )}
    </div>
  );
}
